'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Play } from 'lucide-react'
import type { Persona, Video, VisaType } from '@/payload-types'

export function relationName(item: number | VisaType | Persona): string | null {
  if (typeof item === 'number') return null
  return item.name || null
}

export function relationSlug(item: number | VisaType | Persona): string | null {
  if (typeof item === 'number') return null
  return item.slug || null
}

export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || Number.isNaN(Number(seconds))) return null
  const total = Math.round(Number(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function VideoThumbnail({ youtubeId, title }: { youtubeId: string; title: string }) {
  const [src, setSrc] = useState(`https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`)

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={title}
      loading="lazy"
      // mqdefault, not hqdefault: it is the only always-present fallback that
      // isn't padded out to 4:3, whose padding would read as black bars once the
      // image is scaled to the frame's height.
      onError={() => setSrc(`https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`)}
      // Height fills the frame and the width follows the aspect ratio: a portrait
      // clip leaves brand-blue bars at the sides, a wide thumbnail is cropped
      // there instead. Preflight caps images at 100% width, hence max-w-none.
      className="absolute top-0 left-1/2 h-full w-auto max-w-none -translate-x-1/2"
    />
  )
}

/**
 * The blue asked for in issue #26 for the video frame and its title. It sits a
 * hair off the site's --color-brand-blue (#24253a) — indistinguishable by eye,
 * but written out here so the frame matches the brand guide exactly.
 */
const BRAND_BLUE = '#23233d'

/** The thumbnail box. In a grid its aspect ratio sets the height; in a carousel
 * every card shares one height so clips and full-length videos sit on one line. */
export function videoMediaClass(format: Video['format'], layout: 'grid' | 'carousel' = 'grid') {
  const base =
    'relative w-full overflow-hidden rounded-2xl shadow-sm transition-shadow duration-300 group-hover:shadow-2xl'

  if (layout === 'grid') {
    return `${base} ${format === 'full' ? 'aspect-video' : 'aspect-3/4'}`
  }

  return `${base} h-75 md:h-88`
}

/** Shared card visuals — thumbnail with badges, and the title captioned beneath it. */
export function VideoCardContent({
  video,
  layout = 'grid',
}: {
  video: Video
  layout?: 'grid' | 'carousel'
}) {
  const duration = formatDuration(video.durationSeconds)
  const visaLabels = video.visaTypes?.map((v) => relationName(v)).filter(Boolean).slice(0, 2) || []
  const isFull = video.format === 'full'

  return (
    <>
      <div
        className={videoMediaClass(video.format, layout)}
        style={{ backgroundColor: BRAND_BLUE }}
      >
        <VideoThumbnail youtubeId={video.youtubeId} title={video.title} />

        <div className="relative z-10 flex h-full flex-col p-4 md:p-5">
          <div className="flex items-start justify-between gap-2">
            <span
              className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white ${
                isFull ? '' : 'bg-brand-gold'
              }`}
              // The badge can overlap a side bar, so it takes the frame's blue.
              style={isFull ? { backgroundColor: BRAND_BLUE } : undefined}
            >
              <Play className="h-3 w-3 fill-current" />
              {isFull ? 'Full-length' : 'Clip'}
            </span>
            {duration && (
              <span className="rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white/90 backdrop-blur-sm">
                {duration}
              </span>
            )}
          </div>

          <div className="flex flex-1 items-center justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-white group-hover:text-primary group-hover:opacity-100">
              <Play className="ml-0.5 h-6 w-6 fill-current" />
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-2" style={{ color: BRAND_BLUE }}>
        <h3 className="text-[15px] leading-snug font-semibold tracking-tight md:text-base">
          {video.title}
        </h3>
        {visaLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visaLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-brand-blue/10 px-2 py-0.5 text-xs font-medium"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/**
 * Wrapper around the media box and its caption. Carousel cards need an explicit
 * width — the title sits outside the thumbnail now, so a long one would
 * otherwise stretch the card past its thumbnail. The widths match the shared
 * carousel height at each format's aspect ratio (16/9 and 3/4 of h-75/h-88).
 */
export function videoCardClass(format: Video['format'], layout: 'grid' | 'carousel' = 'grid') {
  const base = 'group flex flex-col text-left'

  if (layout === 'grid') return `${base} w-full`

  const carousel = `${base} shrink-0 snap-start`
  // Wide cards are capped so they never outgrow a phone screen.
  return format === 'full' ? `${carousel} w-133 max-w-[85vw] md:w-156` : `${carousel} w-56 md:w-66`
}

/** Card that navigates to the video's own page — used outside the videos index. */
export function VideoCardLink({
  video,
  layout = 'grid',
}: {
  video: Video
  layout?: 'grid' | 'carousel'
}) {
  return (
    <Link href={`/videos/${video.slug}`} className={videoCardClass(video.format, layout)}>
      <VideoCardContent video={video} layout={layout} />
    </Link>
  )
}
